import { createFileRoute } from '@tanstack/react-router';

import { useEffect, useState, useMemo } from 'react';
import { Box, Card, Flex, Heading, Text, Tabs, Badge, Button, Dialog, TextField, IconButton } from '@radix-ui/themes';
import { getAutomationRun, runAutomation } from '~/services/versori-api/automations';
import type { AutomationRun, AutomationArtifact } from '~/services/versori-api/types';
import { useOrganisation } from '~/hooks/useOrganisation';
import { TbFile, TbFileText, TbHistory, TbMovie, TbExternalLink, TbX, TbZoomIn, TbDownload, TbPlus, TbTrash, TbRobot } from 'react-icons/tb';
import { Header } from '~/components/Header';
import { useNavigate } from '@tanstack/react-router';

export const Route = createFileRoute('/automations/$automationId/runs/$runId')({
  component: AutomationRunDetailsPage,
});

interface Artifact {
  name: string;
  url: string;
}

interface RunDetails {
  id: string;
  status: string;
  prompt?: string;
  executionId?: string;
  created_at?: string;
  updated_at?: string;
  result?: any;
  artifacts?: Artifact[];
}

interface Variable {
  name: string;
  value: string;
}

function AutomationRunDetailsPage() {
  const { automationId, runId } = Route.useParams() as { automationId: string; runId: string };
  const { organisation } = useOrganisation();
  const [automationRun, setAutomationRun] = useState<AutomationRun | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isGifModalOpen, setIsGifModalOpen] = useState(false);
  const navigate = useNavigate();
  
  // Variable injection states
  const [transcription, setTranscription] = useState<string>('');
  const [variables, setVariables] = useState<Variable[]>([{ name: '', value: '' }]);
  const [processedTranscription, setProcessedTranscription] = useState<string>('');
  const [isVariableDialogOpen, setIsVariableDialogOpen] = useState(false);
  const [isRunning, setIsRunning] = useState(false);

  // Get status badge color based on status
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'running':
        return 'blue';
      case 'completed':
        return 'green';
      case 'failed':
        return 'red';
      case 'pending':
        return 'yellow';
      default:
        return 'gray';
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      
      if (!organisation?.id || !automationId || !runId) {
        setError('Missing required parameters to fetch run details');
        setIsLoading(false);
        return;
      }
      
      try {
        const runData = await getAutomationRun(organisation.id, automationId, runId);
        setAutomationRun(runData);
        
        // Initialize transcription from prompt if available
        if (runData.prompt) {
          setTranscription(runData.prompt);
          setProcessedTranscription(runData.prompt);
        }
      } catch (err) {
        console.error('Error loading run data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load run data');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [runId, automationId, organisation?.id]);

  // Process the transcription with variables
  const processTemplate = () => {
    let result = transcription;
    
    // Replace all variable placeholders with their values
    variables.forEach(variable => {
      if (variable.name && variable.value) {
        const pattern = new RegExp(`{{{${variable.name}}}}`, 'g');
        result = result.replace(pattern, variable.value);
      }
    });
    
    setProcessedTranscription(result);
  };
  
  // Run automation with processed transcription
  const handleRunAutomation = async () => {
    if (!organisation?.id || !automationId || !processedTranscription) {
      setError("Can't run automation: missing required information");
      return;
    }
    
    setIsRunning(true);
    setError(null);
    
    try {
      const result = await runAutomation(
        organisation.id, 
        automationId, 
        processedTranscription,
        30 // Use default maxSteps value
      );
      
      // Navigate to the new run's page
      if (result && result.id) {
        navigate({ to: `/automations/${automationId}/runs/${result.id}` });
      }
    } catch (err) {
      console.error('Error running automation:', err);
      setError(err instanceof Error ? err.message : 'Failed to run automation');
    } finally {
      setIsRunning(false);
    }
  };
  
  // Handle variable changes
  const handleVariableChange = (index: number, field: 'name' | 'value', value: string) => {
    const newVariables = [...variables];
    newVariables[index][field] = value;
    setVariables(newVariables);
  };
  
  // Add a new variable
  const addVariable = () => {
    setVariables([...variables, { name: '', value: '' }]);
  };
  
  // Remove a variable
  const removeVariable = (index: number) => {
    if (variables.length > 1) {
      const newVariables = [...variables];
      newVariables.splice(index, 1);
      setVariables(newVariables);
    }
  };

  // Use useMemo to only recompute artifacts when automationRun changes
  const {
    gifArtifact,
    summaryArtifact,
    historyArtifact,
    taskArtifact,
    hasVisualContent,
    hasSummary,
    hasArtifacts
  } = useMemo(() => {
    // Helper to get artifact by filename pattern
    const getArtifactByPattern = (pattern: string): AutomationArtifact | undefined => {
      if (!automationRun?.artifacts) return undefined;
      if (!Array.isArray(automationRun.artifacts)) return undefined;
      if (automationRun.artifacts.length === 0) return undefined;
      
      return automationRun.artifacts.find(artifact => artifact.name.includes(pattern));
    };

    // Get specific artifacts
    const gifArtifact = getArtifactByPattern('session.gif');
    const summaryArtifact = getArtifactByPattern('summary.md');
    const historyArtifact = getArtifactByPattern('history.json');
    const taskArtifact = getArtifactByPattern('task.txt');

    // Determine what content we have available
    const hasVisualContent = !!gifArtifact;
    const hasSummary = !!summaryArtifact;
    const hasArtifacts = 
      automationRun?.artifacts && 
      Array.isArray(automationRun.artifacts) && 
      automationRun.artifacts.length > 0;

    return {
      gifArtifact,
      summaryArtifact,
      historyArtifact,
      taskArtifact,
      hasVisualContent,
      hasSummary,
      hasArtifacts
    };
  }, [automationRun]);

  return (
    <Flex direction="column" minHeight="100vh">
      <Header href="/automations" />
      <Box className="w-full max-w-[1200px] mx-auto mt-8">
        <Flex justify="between" align="center" mb="4">
          <Heading size="6">Automation Run Details: </Heading>
          {automationRun && (
            <Badge 
              variant="solid" 
              color={automationRun.status === 'completed' ? 'green' : 
                    automationRun.status === 'failed' ? 'red' : 
                    automationRun.status === 'running' ? 'blue' : 'gray'}
            >
              {automationRun.status.toUpperCase()}
            </Badge>
          )}
        </Flex>
        
        {isLoading && (
          <Card className="p-6 mb-4">
            <Text>Loading run details...</Text>
          </Card>
        )}
        
        {error && (
          <Card className="p-6 mb-4 bg-red-50 text-red-700">
            <Text>{error}</Text>
          </Card>
        )}
        
        {automationRun && (
          <Box>
            {/* Details Section */}
            <Card className="p-6 mb-4">
              <Flex direction="column" gap="4">
                <Flex gap="4" wrap="wrap">
                  <Box style={{ minWidth: '200px', flex: 1 }}>
                    <Text size="2" color="gray">RUN ID: </Text>
                    <Text size="3" weight="medium">{automationRun.id}</Text>
                  </Box>
                  
                  <Box style={{ minWidth: '200px', flex: 1 }}>
                    <Text size="2" color="gray">AUTOMATION ID: </Text>
                    <Text size="3" weight="medium">{automationRun.automationId}</Text>
                  </Box>
                  
                  {automationRun.executionId && (
                    <Box style={{ minWidth: '200px', flex: 1 }}>
                      <Text size="2" color="gray">EXECUTION ID: </Text>
                      <Text size="3" weight="medium">{automationRun.executionId}</Text>
                    </Box>
                  )}
                </Flex>
                
                <Box>
                  <Text size="2" color="gray">PROMPT: </Text>
                  <Card variant="classic" className="mt-1 p-3">
                    <Text>{automationRun.prompt || 'No prompt specified'}</Text>
                  </Card>
                </Box>
                
                {automationRun.createdAt && (
                  <Box>
                    <Text size="2" color="gray">CREATED AT: </Text>
                    <Text>{new Date(automationRun.createdAt).toLocaleString()}</Text>
                  </Box>
                )}
                
                {automationRun.updatedAt && (
                  <Box>
                    <Text size="2" color="gray">UPDATED AT: </Text>
                    <Text>{new Date(automationRun.updatedAt).toLocaleString()}</Text>
                  </Box>
                )}
                
                {automationRun.result && (
                  <Box>
                    <Text size="2" color="gray" mb="1">RESULT: </Text>
                    <pre className="bg-gray-50 p-3 rounded-md overflow-auto">
                      {JSON.stringify(automationRun.result, null, 2)}
                    </pre>
                  </Box>
                )}
                
                {automationRun.error && (
                  <Box className="bg-red-50 p-3 rounded-md">
                    <Text size="2" weight="medium" className="text-red-700" mb="1">ERROR: </Text>
                    <Text className="text-red-700">{automationRun.error}</Text>
                  </Box>
                )}
              </Flex>
            </Card>
            
            {/* Visualization and Summary Row */}
            <Flex gap="4" wrap="wrap" mb="4">
              {hasVisualContent && (
                <Box style={{ flex: 1, minWidth: '300px' }}>
                  <Card className="p-6 h-full">
                    <Flex direction="column" gap="4" style={{ height: '100%' }}>
                      <Heading size="4">Automation Recording: </Heading>
                      
                      <Box className="relative overflow-hidden rounded-md bg-gray-50 border border-gray-200" style={{ flex: 1 }}>
                        <img 
                          src={gifArtifact!.url} 
                          alt="Automation Recording"
                          className="w-full cursor-pointer" 
                          style={{ maxHeight: '400px', objectFit: 'contain' }}
                          onClick={() => setIsGifModalOpen(true)}
                        />
                      </Box>
                    </Flex>
                  </Card>
                </Box>
              )}
              
              {hasSummary && (
                <Box style={{ flex: 1, minWidth: '300px' }}>
                  <Card className="p-6 h-full">
                    <Flex direction="column" gap="4" style={{ height: '100%' }}>
                      <Heading size="4">Automation Summary: </Heading>
                      
                      <Box className="p-4 rounded-md bg-gray-50 border border-gray-200" style={{ flex: 1 }}>
                        <iframe 
                          src={summaryArtifact!.url} 
                          style={{ width: '100%', height: '100%', minHeight: '300px', border: 'none' }}
                          title="Automation Summary"
                        />
                      </Box>
                    </Flex>
                  </Card>
                </Box>
              )}
            </Flex>
            
            {/* All Artifacts Section */}
            {hasArtifacts && (
              <Card className="p-6">
                <Flex direction="column" gap="4">
                  <Heading size="4">All Artifacts: </Heading>
                  <Text size="2" color="gray">The following artifacts are available for this run:</Text>
                  
                  <Box className="overflow-hidden border border-gray-200 rounded-md">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">Artifacts: </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {automationRun.artifacts && automationRun.artifacts.map((artifact, index) => {
                          // Determine the type icon based on file extension or name
                          let icon = <TbFile />;
                          if (artifact.name.includes('.gif')) icon = <TbMovie />;
                          else if (artifact.name.includes('.md')) icon = <TbFileText />;
                          else if (artifact.name.includes('.json')) icon = <TbHistory />;
                          
                          // Extract the filename from the path
                          const fileName = artifact.name.split('/').pop() || artifact.name;
                          
                          return (
                            <tr key={index}>
                              <td className="px-4 py-3 whitespace-nowrap text-sm">
                                <Flex align="center" gap="2">
                                  {icon}
                                  <a href={artifact.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1">
                                    {fileName}
                                    <TbExternalLink size={14} />
                                  </a>
                                </Flex>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </Box>
                </Flex>
              </Card>
            )}
          </Box>
        )}
      </Box>

      {/* Variables Management Dialog */}
      <Dialog.Root open={isVariableDialogOpen} onOpenChange={setIsVariableDialogOpen}>
        <Dialog.Content style={{ maxWidth: '600px' }}>
          <Dialog.Title>Manage Variables</Dialog.Title>
          <Dialog.Description size="2" mb="4">
            Define variables to use in your transcription with {"{{{"}variable_name{"}}}"}  syntax.
          </Dialog.Description>
          
          <Flex direction="column" gap="3">
            {variables.map((variable, index) => (
              <Flex key={index} gap="3" align="center">
                <TextField.Root style={{ flex: 1 }}>
                  <TextField.Slot>
                    <input 
                      placeholder="Variable name" 
                      value={variable.name}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleVariableChange(index, 'name', e.target.value)}
                      className="w-full px-3 py-2 text-sm border-none outline-none"
                    />
                  </TextField.Slot>
                </TextField.Root>
                <TextField.Root style={{ flex: 1 }}>
                  <TextField.Slot>
                    <input 
                      placeholder="Value" 
                      value={variable.value}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleVariableChange(index, 'value', e.target.value)}
                      className="w-full px-3 py-2 text-sm border-none outline-none"
                    />
                  </TextField.Slot>
                </TextField.Root>
                <IconButton 
                  size="1" 
                  variant="soft" 
                  color="red"
                  onClick={() => removeVariable(index)}
                  disabled={variables.length <= 1}
                >
                  <TbTrash />
                </IconButton>
              </Flex>
            ))}
            
            <Button 
              variant="soft" 
              onClick={addVariable}
              className="mt-2"
            >
              <TbPlus /> Add Variable
            </Button>
          </Flex>
          
          <Flex gap="3" mt="4" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray">Cancel</Button>
            </Dialog.Close>
            <Dialog.Close>
              <Button variant="solid" onClick={processTemplate}>Apply Variables</Button>
            </Dialog.Close>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      {/* GIF Modal for zoomed view */}
      {hasVisualContent && (
        <Dialog.Root open={isGifModalOpen} onOpenChange={setIsGifModalOpen}>
          <Dialog.Content 
            style={{ maxWidth: '90vw', maxHeight: '90vh' }}
            onEscapeKeyDown={() => setIsGifModalOpen(false)}
            aria-describedby="gif-modal-description"
          >
            <Flex justify="between" align="center" mb="3">
              <Dialog.Title>Automation Recording</Dialog.Title>
              <Dialog.Close>
                <Button variant="ghost" color="gray" aria-label="Close dialog">
                  <TbX />
                </Button>
              </Dialog.Close>
            </Flex>
            
            <Box 
              className="relative overflow-auto bg-gray-50 border border-gray-200 rounded-md"
              id="gif-modal-description"
            >
              <img 
                src={gifArtifact?.url} 
                alt="Automation Recording"
                style={{ 
                  width: '100%', 
                  height: 'auto', 
                  maxHeight: '80vh',
                  objectFit: 'contain'
                }}
              />
            </Box>
            
            <Flex gap="3" mt="4" justify="end">
              <Dialog.Close>
                <Button variant="soft" autoFocus>Close</Button>
              </Dialog.Close>
              <Button asChild variant="outline">
                <a 
                  href={gifArtifact?.url} 
                  download={`automation-recording-${automationRun?.id}.gif`}
                  aria-label="Download recording"
                >
                  <TbDownload />
                  Download
                </a>
              </Button>
            </Flex>
          </Dialog.Content>
        </Dialog.Root>
      )}
    </Flex>
  );
} 